<template>
  <v-form ref="form" v-model="valid" @input="$emit('valid', $event)">
    <v-row>
      <v-col cols="12">
        <v-select
          v-model.number="form.reason_id"
          name="reason_id"
          :items="reasonsList"
          item-value="id"
          item-text="name"
          :readonly="readonly"
          :label="`${$t('PLANNINGS.REASON')} *`"
          :rules="getRulesByName('reason_id')"
          :error-messages="errors.reason_id"
          @change="setReason"
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="showTimed">
      <v-col cols="12" xs="12" md="6">
        <v-menu
          ref="timeFrom"
          v-model="timeFrom"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="form.time_from"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="form.time_from"
              name="time_from"
              :label="`${$t('PLANNINGS.TIME_FROM')} *`"
              prepend-icon="mdi-clock-time-four-outline"
              readonly
              clearable
              :rules="getRulesByName('time_from')"
              :error-messages="errors.time_from"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-if="timeFrom"
            v-model="form.time_from"
            :allowed-minutes="allowedStep"
            :max="maxTimeFrom"
            format="24hr"
            scrollable
            @click:minute="$refs.timeFrom.save(form.time_from)"
            @input="updateValue('time_from', $event)"
          ></v-time-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" xs="12" md="6">
        <v-menu
          ref="timeTo"
          v-model="timeTo"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="form.time_to"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="form.time_to"
              name="time_to"
              prepend-icon="mdi-clock-time-four-outline"
              :label="`${$t('PLANNINGS.TIME_TO')} *`"
              :rules="getRulesByName('time_to')"
              :error-messages="errors.time_to"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-if="timeTo"
            v-model="form.time_to"
            :allowed-minutes="allowedStep"
            :min="minTimeTo"
            format="24hr"
            scrollable
            @click:minute="$refs.timeTo.save(form.time_to)"
            @input="updateValue('time_to', $event)"
          ></v-time-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-textarea
          v-model="form.info"
          name="info"
          :label="$t('ITEM.ADDITIONAL_INFO')"
          :readonly="readonly"
          :rules="getRulesByName('info')"
          :error-messages="errors.info"
          @input="updateValue('info', $event)"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet class="px-3 py-1" tile outlined>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="form.is_multi_task"
                name="is_multi_task"
                :label="$t('PLANNINGS.PERIODIC')"
                :readonly="readonlyMultiTaskForm"
                :error="errors.is_multi_task"
                @change="updateValue('is_multi_task', $event)"
              ></v-switch>
            </v-col>
            <v-col v-if="form.is_multi_task" cols="12">
              <block-day-multi-tasks-form
                v-model="form"
                :readonly="readonlyMultiTaskForm"
                :errors="errors"
                @input="emitter"
                @update-prop="updateProp(...arguments)"
              />
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <slot v-if="!readonly" name="buttons" :valid="valid" :reset="reset" />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapState } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import formEditable from '@/modules/_global/mixins/form-editable';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { notEmpty } from '@/modules/_global/variables/rules';
import { dateFormat } from '@/modules/_global/filters';
import { LOCKED_TIME } from '../../../common/reasons';

const NULLABLE_TIME = '00:00';

export default {
  name: 'BlockDayForm',

  filters: {
    dateFormat
  },

  components: {
    BlockDayMultiTasksForm: () => import('./BlockDayMultiTasksForm.vue')
  },

  mixins: [formEditable, i18nRefreshData],

  props: {
    editable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      valid: false,
      timeFrom: false,
      timeTo: false,
      form: {
        reason_id: '',
        info: '',
        time_from: null,
        time_to: null,
        is_multi_task: false,
        date_from: null
      }
    };
  },

  computed: {
    ...mapState('planning', ['reasonsList']),
    rules() {
      const timeRules = this.showTimed ? [notEmpty] : [];

      return {
        reason_id: [notEmpty],
        time_from: timeRules,
        time_to: timeRules
      };
    },

    showTimed() {
      return this.form.reason_id === LOCKED_TIME;
    },

    readonlyMultiTaskForm() {
      return this.readonly || Boolean(this.form.id);
    },

    maxTimeFrom() {
      if (this.form.time_to === NULLABLE_TIME) {
        return;
      }

      return this.form.time_to;
    },

    minTimeTo() {
      if (this.form.time_from === NULLABLE_TIME) {
        return;
      }

      return this.form.time_from;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapCacheActions('planning', ['getReasonsList']),
    refreshData() {
      this.getReasonsList();
    },

    allowedStep: m => m % 5 === 0,

    reset() {
      this.$refs.form.reset;
    },

    setReason(reasonId) {
      if (reasonId === LOCKED_TIME) {
        this.updateProp('start_date', this.form.start_date);
        this.updateProp('end_date', this.form.end_date);
      }

      this.updateValue('reason_id', reasonId);
    }
  }
};
</script>

<style scoped></style>
